import { DataOutputStream, FileSystem } from './java/DataStream';
import { Profile } from './Profile';
import { HUD } from './HUD';

/**
 * Achievement system — 50 achievements across 4 categories.
 * Checked after each run ends. Persisted to dotch_achievements.dat.
 * Newly unlocked achievements are queued for in-game notification.
 */

/** Achievement definition */
export class Achievement {
  public readonly id: number;
  public readonly name: string;
  public readonly description: string;
  public readonly category: number;
  public unlocked: boolean;

  constructor(id: number, name: string, desc: string, category: number) {
    this.id = id;
    this.name = name;
    this.description = desc;
    this.category = category;
    this.unlocked = false;
  }
}

export class Achievements {
  private static readonly FILE = 'dotch_achievements.dat';
  private static readonly VERSION = 1;

  // Categories
  static readonly CAT_SURVIVAL = 0;
  static readonly CAT_SKILL = 1;
  static readonly CAT_PERSISTENCE = 2;
  static readonly CAT_CHALLENGE = 3;
  static readonly CAT_NAMES: string[] = ['Survival', 'Skill', 'Persistence', 'Challenge'];

  private static readonly ALL: Achievement[] = [];
  private static readonly newlyUnlocked: Achievement[] = [];

  // ==================== Achievement Definitions ====================

  static init(): void {
    if (Achievements.ALL.length > 0) return;

    // --- SURVIVAL (0-14) ---
    Achievements.def(0, 'First Steps', 'Reach level 5', Achievements.CAT_SURVIVAL);
    Achievements.def(1, 'Getting Warmed Up', 'Reach level 10', Achievements.CAT_SURVIVAL);
    Achievements.def(2, 'Veteran', 'Reach level 15', Achievements.CAT_SURVIVAL);
    Achievements.def(3, 'Endurance', 'Reach level 20', Achievements.CAT_SURVIVAL);
    Achievements.def(4, 'Unstoppable', 'Reach level 30', Achievements.CAT_SURVIVAL);
    Achievements.def(5, 'Minute Man', 'Survive for 1 minute', Achievements.CAT_SURVIVAL);
    Achievements.def(6, 'Five Alive', 'Survive for 5 minutes', Achievements.CAT_SURVIVAL);
    Achievements.def(7, 'Marathon Runner', 'Survive for 10 minutes', Achievements.CAT_SURVIVAL);
    Achievements.def(8, 'Score Chaser', 'Score 5,000 in a single run', Achievements.CAT_SURVIVAL);
    Achievements.def(9, 'High Roller', 'Score 25,000 in a single run', Achievements.CAT_SURVIVAL);
    Achievements.def(10, 'Score Legend', 'Score 100,000 in a single run', Achievements.CAT_SURVIVAL);
    Achievements.def(11, 'Boss Slayer', 'Defeat your first boss', Achievements.CAT_SURVIVAL);
    Achievements.def(12, 'Boss Hunter', 'Defeat 5 bosses in a single run', Achievements.CAT_SURVIVAL);
    Achievements.def(13, 'Wave Rider', 'Reach wave 3', Achievements.CAT_SURVIVAL);
    Achievements.def(14, 'Wave Master', 'Reach wave 5', Achievements.CAT_SURVIVAL);

    // --- SKILL (15-29) ---
    Achievements.def(15, 'Untouchable', 'Survive 30 seconds without damage', Achievements.CAT_SKILL);
    Achievements.def(16, 'Iron Will', 'Survive 60 seconds without damage', Achievements.CAT_SKILL);
    Achievements.def(17, 'Ghost', 'Survive 2 minutes without damage', Achievements.CAT_SKILL);
    Achievements.def(18, 'Flawless Boss', 'Defeat a boss without taking any damage', Achievements.CAT_SKILL);
    Achievements.def(19, 'Minimalist', 'Reach level 5 without buying upgrades', Achievements.CAT_SKILL);
    Achievements.def(20, 'Speedster', 'Use the dash ability 50 times in one run', Achievements.CAT_SKILL);
    Achievements.def(21, 'Bullet Time', 'Use slow-motion 3 times in one run', Achievements.CAT_SKILL);
    Achievements.def(22, 'Shield Breaker', 'Have your shield absorb 5 hits in one run', Achievements.CAT_SKILL);
    Achievements.def(23, 'Triple Multiplier', 'Reach x3 score multiplier', Achievements.CAT_SKILL);
    Achievements.def(24, 'Close Call', 'Survive a run with less than 5 health remaining', Achievements.CAT_SKILL);
    Achievements.def(25, 'Pacifist Start', 'Reach level 3 without taking damage', Achievements.CAT_SKILL);
    Achievements.def(26, 'No Shield Needed', 'Reach level 10 without shield activating', Achievements.CAT_SKILL);
    Achievements.def(27, 'Speed Demon', 'Buy max speed upgrades in a single run', Achievements.CAT_SKILL);
    Achievements.def(28, 'Tank Build', 'Buy max health upgrades in a single run', Achievements.CAT_SKILL);
    Achievements.def(29, 'Efficient', 'Score 10,000 with 0 upgrades purchased', Achievements.CAT_SKILL);

    // --- PERSISTENCE (30-42) ---
    Achievements.def(30, 'Newcomer', 'Play 10 games', Achievements.CAT_PERSISTENCE);
    Achievements.def(31, 'Regular', 'Play 50 games', Achievements.CAT_PERSISTENCE);
    Achievements.def(32, 'Dedicated', 'Play 100 games', Achievements.CAT_PERSISTENCE);
    Achievements.def(33, 'Veteran Player', 'Play 500 games', Achievements.CAT_PERSISTENCE);
    Achievements.def(34, 'Accumulator', 'Reach 50,000 total lifetime score', Achievements.CAT_PERSISTENCE);
    Achievements.def(35, 'Score Hoarder', 'Reach 500,000 total lifetime score', Achievements.CAT_PERSISTENCE);
    Achievements.def(36, 'Millionaire', 'Reach 1,000,000 total lifetime score', Achievements.CAT_PERSISTENCE);
    Achievements.def(37, 'Die Hard', 'Die 50 times', Achievements.CAT_PERSISTENCE);
    Achievements.def(38, 'Persistent', 'Die 200 times', Achievements.CAT_PERSISTENCE);
    Achievements.def(39, 'Level 5', 'Reach profile level 5', Achievements.CAT_PERSISTENCE);
    Achievements.def(40, 'Level 10', 'Reach profile level 10', Achievements.CAT_PERSISTENCE);
    Achievements.def(41, 'Level 25', 'Reach profile level 25', Achievements.CAT_PERSISTENCE);
    Achievements.def(42, 'Boss Veteran', 'Defeat 25 bosses total', Achievements.CAT_PERSISTENCE);

    // --- CHALLENGE (43-49) ---
    Achievements.def(43, 'Hard Mode', 'Complete level 10 on Hard difficulty', Achievements.CAT_CHALLENGE);
    Achievements.def(44, 'Insane Mode', 'Complete level 10 on Insane difficulty', Achievements.CAT_CHALLENGE);
    Achievements.def(45, 'Hard Veteran', 'Reach level 20 on Hard difficulty', Achievements.CAT_CHALLENGE);
    Achievements.def(46, 'Insane Veteran', 'Reach level 15 on Insane difficulty', Achievements.CAT_CHALLENGE);
    Achievements.def(47, 'All Rounder', 'Play at least 10 games on each difficulty', Achievements.CAT_CHALLENGE);
    Achievements.def(48, 'Hard High Score', 'Score 50,000 on Hard difficulty', Achievements.CAT_CHALLENGE);
    Achievements.def(49, 'Insane Survivor', 'Survive 5 minutes on Insane difficulty', Achievements.CAT_CHALLENGE);

    Achievements.load();
  }

  private static def(id: number, name: string, desc: string, cat: number): void {
    Achievements.ALL.push(new Achievement(id, name, desc, cat));
  }

  // ==================== Public API ====================

  static getAll(): Achievement[] { return Achievements.ALL; }
  static getCount(): number { return Achievements.ALL.length; }

  static getUnlockedCount(): number {
    let c = 0;
    for (const a of Achievements.ALL) if (a.unlocked) c++;
    return c;
  }

  static getUnlockedInCategory(cat: number): number {
    let c = 0;
    for (const a of Achievements.ALL) if (a.category === cat && a.unlocked) c++;
    return c;
  }

  static getTotalInCategory(cat: number): number {
    let c = 0;
    for (const a of Achievements.ALL) if (a.category === cat) c++;
    return c;
  }

  /** Get newly unlocked achievements (since last call). Clears the queue. */
  static popNewlyUnlocked(): Achievement[] {
    const result: Achievement[] = Achievements.newlyUnlocked.slice();
    Achievements.newlyUnlocked.length = 0;
    return result;
  }

  static hasNew(): boolean { return Achievements.newlyUnlocked.length !== 0; }

  // ==================== Check Conditions ====================

  /**
   * Called after every run ends (from Game.java death sequence).
   * Checks all achievement conditions against current run + profile data.
   */
  static checkAfterRun(runScore: number, runLevel: number, runTimeTicks: number,
                       runUpgrades: number, runHealthUps: number, runSpeedUps: number,
                       runBossesDefeated: number, runDamageTaken: number,
                       runLongestStreakTicks: number, difficulty: number,
                       runWave: number): void {
    const runTimeSec = Math.trunc(runTimeTicks / 60);
    const streakSec = Math.trunc(runLongestStreakTicks / 60);

    // --- SURVIVAL ---
    Achievements.checkUnlock(0, runLevel >= 5);
    Achievements.checkUnlock(1, runLevel >= 10);
    Achievements.checkUnlock(2, runLevel >= 15);
    Achievements.checkUnlock(3, runLevel >= 20);
    Achievements.checkUnlock(4, runLevel >= 30);
    Achievements.checkUnlock(5, runTimeSec >= 60);
    Achievements.checkUnlock(6, runTimeSec >= 300);
    Achievements.checkUnlock(7, runTimeSec >= 600);
    Achievements.checkUnlock(8, runScore >= 5000);
    Achievements.checkUnlock(9, runScore >= 25000);
    Achievements.checkUnlock(10, runScore >= 100000);
    Achievements.checkUnlock(11, runBossesDefeated >= 1);
    Achievements.checkUnlock(12, runBossesDefeated >= 5);
    Achievements.checkUnlock(13, runWave >= 3);
    Achievements.checkUnlock(14, runWave >= 5);

    // --- SKILL ---
    Achievements.checkUnlock(15, streakSec >= 30);
    Achievements.checkUnlock(16, streakSec >= 60);
    Achievements.checkUnlock(17, streakSec >= 120);
    Achievements.checkUnlock(18, runBossesDefeated >= 1 && runDamageTaken === 0);
    Achievements.checkUnlock(19, runLevel >= 5 && runUpgrades === 0);
    // 20, 21, 22 checked during gameplay (need counters — defer to in-run checks)
    Achievements.checkUnlock(23, Profile.getLongestStreak() >= 15); // 15s = STREAK_T3 / 60
    Achievements.checkUnlock(24, runLevel >= 3 && HUD.HEALTH > 0 && HUD.HEALTH < 5);
    Achievements.checkUnlock(25, runLevel >= 3 && runDamageTaken === 0);
    // 26 — no shield break for 10 levels — simplified: no damage for 10 levels is already ghost-tier
    Achievements.checkUnlock(26, runLevel >= 10 && runDamageTaken === 0);
    Achievements.checkUnlock(27, runSpeedUps >= HUD.MAX_TIER);
    Achievements.checkUnlock(28, runHealthUps >= HUD.MAX_TIER);
    Achievements.checkUnlock(29, runScore >= 10000 && runUpgrades === 0);

    // --- PERSISTENCE (profile-based) ---
    Achievements.checkUnlock(30, Profile.getTotalGames() >= 10);
    Achievements.checkUnlock(31, Profile.getTotalGames() >= 50);
    Achievements.checkUnlock(32, Profile.getTotalGames() >= 100);
    Achievements.checkUnlock(33, Profile.getTotalGames() >= 500);
    Achievements.checkUnlock(34, Number(Profile.getTotalScore()) >= 50000);
    Achievements.checkUnlock(35, Number(Profile.getTotalScore()) >= 500000);
    Achievements.checkUnlock(36, Number(Profile.getTotalScore()) >= 1000000);
    Achievements.checkUnlock(37, Profile.getTotalDeaths() >= 50);
    Achievements.checkUnlock(38, Profile.getTotalDeaths() >= 200);
    Achievements.checkUnlock(39, Profile.getLevel() >= 5);
    Achievements.checkUnlock(40, Profile.getLevel() >= 10);
    Achievements.checkUnlock(41, Profile.getLevel() >= 25);
    Achievements.checkUnlock(42, Profile.getTotalBossesDefeated() >= 25);

    // --- CHALLENGE ---
    Achievements.checkUnlock(43, difficulty === 1 && runLevel >= 10);
    Achievements.checkUnlock(44, difficulty === 2 && runLevel >= 10);
    Achievements.checkUnlock(45, difficulty === 1 && runLevel >= 20);
    Achievements.checkUnlock(46, difficulty === 2 && runLevel >= 15);
    Achievements.checkUnlock(47, Profile.getAttempts(0) >= 10
      && Profile.getAttempts(1) >= 10
      && Profile.getAttempts(2) >= 10);
    Achievements.checkUnlock(48, difficulty === 1 && runScore >= 50000);
    Achievements.checkUnlock(49, difficulty === 2 && runTimeSec >= 300);

    if (Achievements.newlyUnlocked.length !== 0) {
      Achievements.save();
    }
  }

  private static checkUnlock(id: number, condition: boolean): void {
    if (id >= 0 && id < Achievements.ALL.length) {
      const a = Achievements.ALL[id];
      if (!a.unlocked && condition) {
        a.unlocked = true;
        Achievements.newlyUnlocked.push(a);
      }
    }
  }

  // ==================== Progress Helpers ====================

  /** Returns progress toward an achievement (0.0-1.0), or -1 if not applicable */
  static getProgress(id: number): number {
    const a = Achievements.ALL[id];
    if (a.unlocked) return 1;

    switch (id) {
      // Survival: level-based
      case 0: return Profile.getHighLevel(Achievements.bestDiff()) / 5;
      case 1: return Profile.getHighLevel(Achievements.bestDiff()) / 10;
      case 2: return Profile.getHighLevel(Achievements.bestDiff()) / 15;
      case 3: return Profile.getHighLevel(Achievements.bestDiff()) / 20;
      case 4: return Profile.getHighLevel(Achievements.bestDiff()) / 30;

      // Score-based
      case 8: return Profile.getHighScore(Achievements.bestDiff()) / 5000;
      case 9: return Profile.getHighScore(Achievements.bestDiff()) / 25000;
      case 10: return Profile.getHighScore(Achievements.bestDiff()) / 100000;

      // Persistence: games
      case 30: return Profile.getTotalGames() / 10;
      case 31: return Profile.getTotalGames() / 50;
      case 32: return Profile.getTotalGames() / 100;
      case 33: return Profile.getTotalGames() / 500;

      // Persistence: score
      case 34: return Number(Profile.getTotalScore()) / 50000;
      case 35: return Number(Profile.getTotalScore()) / 500000;
      case 36: return Number(Profile.getTotalScore()) / 1000000;

      // Persistence: deaths
      case 37: return Profile.getTotalDeaths() / 50;
      case 38: return Profile.getTotalDeaths() / 200;

      // Profile level
      case 39: return Profile.getLevel() / 5;
      case 40: return Profile.getLevel() / 10;
      case 41: return Profile.getLevel() / 25;

      // Bosses
      case 42: return Profile.getTotalBossesDefeated() / 25;
      case 11: return Math.min(Profile.getTotalBossesDefeated(), 1);

      default: return -1;
    }
  }

  private static bestDiff(): number {
    let best = 0;
    for (let i = 1; i < 3; i++) {
      if (Profile.getHighLevel(i) > Profile.getHighLevel(best)) best = i;
    }
    return best;
  }

  // ==================== Persistence ====================

  private static save(): void {
    try {
      const out = new DataOutputStream(Achievements.FILE);
      out.writeInt(Achievements.VERSION);
      out.writeInt(Achievements.ALL.length);
      for (const a of Achievements.ALL) {
        out.writeBoolean(a.unlocked);
      }
      out.close();
    } catch {
      // Silent
    }
  }

  private static load(): void {
    const in_ = FileSystem.open(Achievements.FILE);
    if (in_ === null) return;
    try {
      const version = in_.readInt();
      void version;
      const count = in_.readInt();
      const toRead = Math.min(count, Achievements.ALL.length);
      for (let i = 0; i < toRead; i++) {
        Achievements.ALL[i].unlocked = in_.readBoolean();
      }
      // Skip any extra if file has more than current definitions
      for (let i = toRead; i < count; i++) {
        in_.readBoolean();
      }
    } catch {
      // Corrupted — keep defaults
    } finally {
      in_.close();
    }
  }

  static resetAll(): void {
    for (const a of Achievements.ALL) a.unlocked = false;
    Achievements.newlyUnlocked.length = 0;
    Achievements.save();
  }
}

type AchievementAlias = Achievement;

/** Java's nested type was `Achievements.Achievement` — keep that spelling usable too. */
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Achievements {
  export type Achievement = AchievementAlias;
}
